<template>
    <div :data-ref="`blood-result-form-${bloodResultId}`">
        <v-col cols="12" xl="10">
            <v-form ref="form" v-model="bloodResultForm">
                <v-row v-if="bloodResult.id !== 'new'">
                    <v-col cols="12" class="pb-0">
                        <h5>{{ t('patients::form.title.result_colour') }}</h5>
                    </v-col>
                    <v-col cols="12" lg="4" class="mb-4">
                        <div :class="`blood-status ${bloodResult.color_result}--text`">
                            <div :class="`status ${bloodResult.color_result}--text`">
                                <v-icon>icon icon-blood-result</v-icon>
                            </div>
                            <span v-text="bloodResult.color_result"></span>
                        </div>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" class="pb-0">
                        <h5>{{ t('patients::form.title.labaratory') }}</h5>
                    </v-col>
                    <v-col cols="12" lg="4" class="pb-0">
                        <b-date-picker-store
                            to-mutate="patients::bloodResult.date_taken"
                            :label="t('patients::form.label.date_taken')"
                            :rules="validationRules.date_taken"
                            :readonly="readOnlyRules"
                            mandatory
                        ></b-date-picker-store>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" lg="4" class="pb-0">
                        <b-select
                            to-mutate="patients::bloodResult.blood_result_provider_id"
                            :label="
                                bloodResultId === 'new' || !providerDropDownDisabled
                                    ? t('patients::form.label.blood_providers')
                                    : t('patients::grid.label.blood_result_provider')
                            "
                            :select-items="bloodProvidersOptions ? bloodProvidersOptions : []"
                            :rules="validationRules.blood_result_provider_id"
                            :readonly="readOnlyRules"
                            :disabled="providerDropDownDisabled"
                            component-name="v-autocomplete"
                        >
                            <template #item="{ item, on, attrs }">
                                <v-list-item v-bind="attrs" v-on="on">
                                    <v-list-item-content>
                                        <v-list-item-title> {{ item.text }} ({{ item.type }}) </v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                            </template>
                            <template #selection="{ item, on, attrs }">
                                <v-chip v-bind="attrs" v-on="on"> {{ item.text }} ({{ item.type }}) </v-chip>
                            </template>
                        </b-select>
                    </v-col>
                    <v-col v-show="bloodResultId && bloodResultId !== 'new'" cols="12" lg="4" class="pb-0">
                        <b-input
                            to-mutate="patients::bloodResult.operator.name"
                            :label="t('patients::form.label.operator')"
                            :readonly="readOnlyRules"
                            disabled
                            mandatory
                        ></b-input>
                    </v-col>
                    <v-col cols="12" lg="4" class="pb-0">
                        <b-date-picker-store
                            to-mutate="patients::bloodResult.date_received"
                            :rules="validationRules.date_received"
                            :label="t('patients::form.label.data_received')"
                            :readonly="readOnlyRules"
                        ></b-date-picker-store>
                    </v-col>
                    <v-col cols="12" lg="4" class="pb-0">
                        <b-input
                            to-mutate="patients::bloodResult.sample_ref"
                            :label="t('patients::form.label.sample_ref')"
                            :readonly="readOnlyRules"
                        ></b-input>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" class="pb-0">
                        <h5>{{ t('patients::form.title.result') }}</h5>
                    </v-col>
                    <v-col cols="12" lg="4" class="pb-0">
                        <b-input
                            to-mutate="patients::bloodResult.platelets"
                            :label="t('patients::form.label.platelets')"
                            :rules="validationRules.platelets"
                            :is-number="true"
                            :readonly="readOnlyRules"
                            type="number"
                            mandatory
                        ></b-input>
                    </v-col>
                    <v-col cols="12" lg="4" class="pb-0">
                        <b-input
                            to-mutate="patients::bloodResult.white_blood_count"
                            :label="t('patients::form.label.white_blood_count')"
                            :rules="validationRules.white_blood_count"
                            :is-number="true"
                            :readonly="readOnlyRules"
                            type="number"
                            mandatory
                        ></b-input>
                    </v-col>
                    <v-col cols="12" lg="4" class="pb-0">
                        <b-input
                            to-mutate="patients::bloodResult.neutrophils"
                            :label="t('patients::form.label.neutrophils')"
                            :rules="validationRules.neutrophils"
                            :readonly="readOnlyRules"
                            type="number"
                            mandatory
                        ></b-input>
                    </v-col>
                    <v-col cols="12">
                        <b-alert :message="t('patients::form.info.blood_result_metrics')"></b-alert>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" class="pb-0">
                        <h5>{{ t('patients::form.title.memos') }}</h5>
                    </v-col>
                    <v-col cols="12" class="pb-0">
                        <b-textarea
                            to-mutate="patients::bloodResult.memo"
                            :label="t('patients::form.label.memo')"
                            :rules="validationRules.memo"
                            :readonly="readOnlyRules"
                            mandatory
                            rows="5"
                        ></b-textarea>
                    </v-col>
                    <v-col cols="12" lg="4" class="pb-0">
                        <b-input
                            to-mutate="patients::bloodResult.notification_to"
                            :label="t('patients::form.label.send_notification_to')"
                            :readonly="readOnlyRules"
                        ></b-input>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <b-alert :message="t('patients::form.info.blood_result_notifications')"></b-alert>
                    </v-col>
                </v-row>
            </v-form>
        </v-col>
        <v-row>
            <v-col cols="12" class="justify-content-start d-flex py-4">
                <v-btn rounded text color="primary" @click="goBack()">
                    {{ t('patients::button.cancel') }}
                </v-btn>
                <v-btn
                    v-if="can('api.v1.patients.blood-results.update') || can('api.v1.patients.blood-results.store')"
                    color="primary"
                    :disabled="readOnlyRules"
                    :loading="bloodResultFormLoading"
                    class="ml-4"
                    rounded
                    @click="saveBloodResult()"
                >
                    {{
                        !bloodResult.id || bloodResult.id === 'new'
                            ? t('patients::button.submit')
                            : t('patients::button.save')
                    }}
                </v-btn>
            </v-col>
        </v-row>
    </div>
</template>

<script>
export default {
    filters: {
        capitalize: function (value) {
            if (!value) {
                return '';
            }
            value = value.toString();
            return value.charAt(0).toUpperCase() + value.slice(1);
        },
    },
    data() {
        return {
            bloodResultForm: false,
            bloodResultFormLoading: false,
            validationRules: {
                // blood_result_provider_id: [(v) => !!v || t('default::validation.field.required')],
                // sample_ref: [(v) => !!v || t('default::validation.field.required')],
                platelets: [(v) => !!v || t('default::validation.field.required')],
                neutrophils: [(v) => !!v || t('default::validation.field.required')],
                date_taken: [
                    (v) => !!v || t('default::validation.field.required'),
                    (v) => {
                        if (v) {
                            const selectDate = v.split('.');
                            const selectFormatDate = `${selectDate[2]}-${selectDate[1]}-${selectDate[0]}`;
                            // const selctetDate = moment(selectFormatDate).toDate();
                            const currentDate = moment().toDate();
                            return (
                                moment(selectFormatDate).isBefore(currentDate) ||
                                t('users::validation.field.date_in_past')
                            );
                        }
                        return false || t('users::validation.field.date_in_past');
                    },
                ],
                date_received: [
                    // (v) => !!v || t('default::validation.field.required'),
                    (v) => {
                        if (v) {
                            const selectDate = v.split('.');
                            const selectFormatDate = `${selectDate[2]}-${selectDate[1]}-${selectDate[0]}`;
                            const currentDate = moment().toDate();
                            return (
                                moment(selectFormatDate).isBefore(currentDate) ||
                                t('users::validation.field.date_in_past')
                            );
                        }
                        return true || t('users::validation.field.date_in_past');
                    },
                ],
                white_blood_count: [(v) => !!v || t('default::validation.field.required')],
                memo: [(v) => !!v || t('default::validation.field.required')],
            },
        };
    },
    computed: {
        bloodResultId() {
            return this.$store.state.patients.bloodResult.id;
        },
        bloodResult() {
            return this.$store.state.patients.bloodResult;
        },
        provider() {
            return this.$store.state.patients.bloodResult.blood_result_provider;
        },
        providerQcMode() {
            return this.$store.state.patients.bloodResult.blood_result_provider
                ? this.$store.state.patients.bloodResult.blood_result_provider.visible_in_qc_list
                : false;
        },
        providerDropDownDisabled() {
            return this.providerQcMode || this.bloodResult.provider_type === 'spire';
        },
        patientId() {
            return this.$store.state.patients.patient.id;
        },
        readOnlyRules() {
            if (this.bloodResult && this.bloodResult.editable && this.can('api.v1.patients.blood-results.update')) {
                return false;
            }

            if (this.bloodResult.id === 'new') {
                return false;
            }

            if (this.bloodResult && !this.bloodResult.editable) {
                return true;
            }
            return !this.can('api.v1.patients.blood-results.update') && this.can('api.v1.patients.blood-results.show');
        },
        bloodProvidersOptions() {
            return this.$store.state.patients.bloodProvidersOptions.map((x) => ({
                value: x.id,
                text: x.name,
                type: x.type,
                disabled: !x.is_registered,
            }));
        },
    },
    watch: {
        bloodResultId() {
            this.$store.dispatch('patients/resetErrorMessages');
            this.$refs.form.resetValidation();
        },
    },
    mounted() {
        this.loadBloodProviders();
    },
    methods: {
        loadBloodProviders() {
            const isManual = !this.providerQcMode || this.bloodResult.provider_type !== 'spire';
            const isEditMode = !(this.bloodResultId === 'new' || !this.bloodResultId);
            let meta = new Object({
                sort: 'name',
            });
            const data = new Array();
            //Load for Manual
            if (isManual) {
                meta.filtered_options = 1;
                meta.is_registered = 1;
                this.$store.dispatch('patients/loadBloodProvidersOptions', meta).then(() => {
                    if (isEditMode) {
                        this.$store.dispatch('patients/addBloodProvidersOptions', this.provider);
                    }
                });
            }
            //Load for Automatic
            if (!isManual) {
                data.push(this.provider);
                return this.$store.dispatch('patients/setBloodProvidersOptions', data);
            }
        },
        goBack() {
            this.$store.dispatch('patients/loadBloodResult', {
                id: null,
                date_taken: null,
                date_received: null,
                blood_result_provider_id: null,
                sample_ref: null,
                platelets: null,
                neutrophils: null,
                white_blood_count: null,
                memo: null,
                notification_to: null,
                blood_result_provider: {
                    visible_in_qc_list: false,
                },
            });
        },
        saveBloodResult() {
            this.bloodResultFormLoading = true;
            const data = Object.assign({}, this.bloodResult);

            if (data.id === 'new') {
                data.id = null;
            }

            data.blood_result_provider = data.blood_result_provider_id;
            data.patient_id = this.patientId;

            this.$store.dispatch('patients/resetErrorMessages');

            let routeUrl = null;

            if (data.id) {
                routeUrl = route('api.v1.patients.blood-results.update', {
                    patient: this.patientId,
                    blood_result: data.id,
                });
            } else {
                routeUrl = route('api.v1.patients.blood-results.store', {
                    patient: this.patientId,
                });
            }

            axios({
                method: data.id ? 'put' : 'post',
                url: routeUrl,
                data,
            })
                .then(() => {
                    this.$root.notify(
                        'info',
                        t('default::notification.success'),
                        data.id
                            ? t('patients::notification.blood_result_updated')
                            : t('patients::notification.blood_result_added')
                    );

                    if (!this.bloodResult.id || this.bloodResult.id === 'new') {
                        this.$store.dispatch('patients/resetErrorMessages');
                        this.$refs.form.resetValidation();
                    }
                    this.$store.dispatch('patients/reloadPatientData');
                    this.goBack();
                })
                .catch((error) => {
                    if (!error.response) {
                        return;
                    }

                    if (error.response.status === 422) {
                        this.$store.dispatch('patients/setErrorMessages', {
                            errors: error.response.data.errors,
                            isBloodResults: true,
                        });
                    } else {
                        this.$root.notify('error', error.response.data.error);
                    }
                })
                .finally(() => _.delay(() => (this.bloodResultFormLoading = false), 400));
        },
    },
};
</script>

<style></style>
